import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CadenciaModal from "../../../core/modals/todoModal";
import CreateEditModal from "./CreateEditModal";
import { message } from 'antd';
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import DraggablePortal from "./DraggablePortal";
import { API_URL } from '../../../environment';

interface Stage {
  id: number;
  nome_funil: string;
  funil: string;
  ordem: number;
  closer: string;
  atendimento_humano: string;
  atendimento_finalizado?: string | null;
}

const TodoAdmin = () => {

  // --- Estados do Admin ---
  const [times, setTimes] = useState<any[]>([]);
  const [selectedTimeId, setSelectedTimeId] = useState<string>("");

  const [funnels, setFunnels] = useState<Array<{ id: number; page: string; closer?: string; etapa_humana?: string; receptor_wp?: string | null }>>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [currentStage, setCurrentStage] = useState("");
  const [allCadencias, setAllCadencias] = useState<any[]>([]);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [modalType, setModalType] = useState<'etapa' | 'funil'>('etapa');
  const [initialName, setInitialName] = useState('');
  const [currentFunnel, setCurrentFunnel] = useState('');
  const [modalShow, setModalShow] = useState(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [applying, setApplying] = useState(false);
  const [closerAtivo, setCloserAtivo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // --- Busca Times ao iniciar ---
  useEffect(() => {
    const fetchTimes = async () => {
      const id_grupo = localStorage.getItem("id_grupo");
      if (!id_grupo) return;
      try {
        const res = await fetch(`${API_URL}/pegartodostimes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_grupo }),
        });
        if (res.ok) {
          const data = await res.json();
          setTimes(data);
          if (Array.isArray(data) && data.length > 0) {
            setSelectedTimeId(prev => prev || String(data[0].id));
          }
        }
      } catch (err) {
        console.error("Erro ao buscar times:", err);
      }
    };
    fetchTimes();
  }, []);

  // --- Limpa dados quando troca o time ---
  useEffect(() => {
    setFunnels([]);
    setStages([]);
    setAllCadencias([]);
    setCloserAtivo(false);
  }, [selectedTimeId]);


  const toggleCloserFunil = async (nome_funil: string, ativado: boolean) => {
    if (!selectedTimeId) return;
    try {
      await fetch(`${API_URL}/habilitar_closer_funil`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente: selectedTimeId,
          nome_funil,
          ativo: !ativado
        }),
      });
      fetchFunnels();
    } catch (err) {
      message.error("Erro ao alterar closer no funil");
    }
  };


  const toggleCloserEtapa = async (id_etapa: number, ativado: boolean, nome_etapa: string, nome_funil: string) => {
    if (!selectedTimeId) return;
    try {
      await fetch(`${API_URL}/habilitar_closer_etapa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente: selectedTimeId,
          nome_etapa,
          nome_funil,
          ativo: !ativado
        }),
      });
      fetchStages();
    } catch (err) {
      message.error("Erro ao alterar closer da etapa");
    }
  };

  // Etapa "Atendimento finalizado" (29/10/2026): uma por funil; card nela sai da lista do
  // chat e aparece só no filtro "Finalizados". Clicar de novo na marcada desmarca.
  const definirEtapaAtendimentoFinalizado = async (nome_etapa: string, funil: string, jaMarcada: boolean) => {
    try {
      const id_cliente = localStorage.getItem("id_cliente");
      await fetch(`${API_URL}/definir_atendimento_finalizado`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente, nome_etapa, nome_funil: funil, ativo: !jaMarcada })
      });
      fetchStages();
    } catch (err) { message.error("Erro ao marcar atendimento finalizado"); }
  };

  const definirEtapaAtendimentoHumano = async (nome_etapa: string, funil: string) => {
    if (!selectedTimeId) return;
    try {
      await fetch(`${API_URL}/definir_atendimento_humano`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente: selectedTimeId,
          nome_etapa,
          nome_funil: funil
        })
      });
      fetchStages();
      fetchFunnels();
    } catch (err) {
      message.error("Erro ao marcar etapa como atendimento humano");
    }
  };

  // Define este funil como o RECEPTOR de WhatsApp do time. E exclusivo: o backend
  // zera os demais e marca so este, porque cada time tem um unico funil receptor.
  const definirReceptorWp = async (nome_funil: string) => {
    if (!selectedTimeId) return;
    if (!window.confirm(`Passar a receber os leads do WhatsApp no funil "${nome_funil}"?

O funil receptor atual deixa de receber.`)) return;
    try {
      const res = await fetch(`${API_URL}/definir_receptor_wp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente: selectedTimeId, nome_funil }),
      });
      if (res.ok) { message.success(`"${nome_funil}" agora e o funil receptor de WhatsApp`); fetchFunnels(); }
      else { const data = await res.json(); message.error(data.error || "Erro ao definir o funil receptor"); }
    } catch (err) { message.error("Erro de conexao ao definir o funil receptor"); }
  };

  const handleDeleteFunil = async (nomeFunil: string) => {
    if (!window.confirm("Excluir este funil?")) return;
    if (!selectedTimeId) return;
    try {
      const res = await fetch(`${API_URL}/deletarfunil`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome_funil: nomeFunil, id_cliente: selectedTimeId }),
      });
      if (res.ok) { message.success("Funil excluído"); fetchFunnels(); }
      else { const data = await res.json(); message.error(data.error || "Erro ao excluir"); }
    } catch (err) { message.error("Erro de conexão"); }
  };

  const handleDeleteEtapa = async (nomeFunil: string, nomeEtapa: string) => {
    if (!window.confirm("Excluir esta etapa?")) return;
    if (!selectedTimeId) return;
    try {
      const res = await fetch(`${API_URL}/deletaretapa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome_funil: nomeFunil, nome_etapa: nomeEtapa, id_cliente: selectedTimeId }),
      });
      if (res.ok) { message.success("Etapa excluída"); fetchStages(); }
      else { const data = await res.json(); message.error(data.error || "Erro ao excluir"); }
    } catch (err) { message.error("Erro de conexão"); }
  };

  const openCreateFunilModal = () => {
    if (!selectedTimeId) { message.warning("Selecione um time primeiro."); return; }
    setModalType('funil'); setIsEdit(false); setInitialName(''); setActiveModal('createEdit'); setModalShow(true);
  };
  const openCreateEtapaModal = (funil: string) => { setModalType('etapa'); setIsEdit(false); setInitialName(''); setCurrentFunnel(funil); setActiveModal('createEdit'); setModalShow(true); };
  const openEditFunilModal = (funilName: string) => { setModalType('funil'); setIsEdit(true); setInitialName(funilName); setActiveModal('createEdit'); setModalShow(true); };
  const openEditEtapaModal = (etapaName: string, funilName: string) => { setModalType('etapa'); setIsEdit(true); setInitialName(etapaName); setCurrentFunnel(funilName); setActiveModal('createEdit'); setModalShow(true); };


  const fetchAllCadencias = async (force = false) => {
    if (!selectedTimeId) return;
    try {
      const response = await fetch(`${API_URL}/pegarcadencias_todas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente: selectedTimeId }),
      });
      if (!response.ok) throw new Error("Erro ao buscar todas as cadências");
      const data = await response.json();
      if (force || !modalShow) setAllCadencias(data);
    } catch (error) {
      console.error("Erro ao buscar todas as cadências:", error);
    }
  };

  // Atualiza periodicamente apenas se tiver time selecionado
  useEffect(() => {
    if (selectedTimeId) {
      fetchAllCadencias();
      const interval = setInterval(fetchAllCadencias, 5000);
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeId]);

  // Verifica closer
  useEffect(() => {
    if (!selectedTimeId) return;
    fetch(`${API_URL}/verificar_closer?id_time=${selectedTimeId}`)
      .then(res => res.json())
      .then(data => {
        setCloserAtivo(data.closer === true || data.closer === "Sim" || data.closer === "true" || data.closer === 1);
      })
      .catch(console.error);
  }, [selectedTimeId]);



  const fetchFunnels = async () => {
    if (!selectedTimeId) return;
    try {
      const response = await fetch(`${API_URL}/pegarpaginas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente: selectedTimeId }),
      });
      if (response.ok) {
        const data = await response.json();
        setFunnels(data);
      }
    } catch (error) {
      console.error("Erro ao buscar funis:", error);
    }
  };

  useEffect(() => {
    fetchFunnels();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeId]);

  const fetchStages = async () => {
    if (isDragging || !selectedTimeId) return;
    try {
      const response = await fetch(`${API_URL}/pegaretapaschat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente: selectedTimeId }),
      });
      if (response.ok) {
        const data = await response.json();
        setStages(data);
      }
    } catch (error) {
      console.error("Erro ao buscar etapas:", error);
    }
  };

  useEffect(() => {
    fetchStages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeId]);

  useEffect(() => {
    if (!selectedTimeId) return;
    const interval = setInterval(() => {
      if (!isDragging && !modalShow) {
        fetchFunnels();
        fetchStages();
      }
    }, 3000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeId, isDragging, modalShow]);


  return (
    <div className="page-wrapper">
      <style>{`.page-wrapper, .content, .card, .accordion, .accordion-item, .row { transform: none !important; }`}</style>
      <div className="content">
        <div className="tp-page-header">
          <h2>Configuração de Funis (Admin)</h2>
          <span className="tp-subtitle">Gerencie funis e etapas de cada time</span>
        </div>

        {/* Team Selector */}
        <div className="tp-team-selector">
          <div className="tp-team-icon"><i className="ti ti-users-group" /></div>
          <div className="tp-team-select-wrap">
            <label>Selecione o Time</label>
            <select
              className="form-select"
              value={selectedTimeId}
              onChange={(e) => setSelectedTimeId(e.target.value)}
            >
              <option value="">-- Selecione um time --</option>
              {times.map((t: any) => (
                <option key={t.id} value={String(t.id)}>{t.nome}</option>
              ))}
            </select>
          </div>
        </div>

        {selectedTimeId && (
          <div className="tp-main-card">
            <div className="tp-main-card-body">
              <div className="mb-3">
                <button className="tp-add-funnel" onClick={() => { setIsEdit(false); openCreateFunilModal(); }}>
                  <span className="tp-add-icon"><i className="ti ti-plus" /></span>
                  Novo Funil
                </button>
              </div>

              <DragDropContext
                onDragStart={() => setIsDragging(true)}
                onDragEnd={async (result: DropResult) => {
                  setIsDragging(false);
                  if (!result.destination) return;
                  const sourceId = result.source.droppableId;
                  const destinationId = result.destination.droppableId;
                  if (sourceId !== destinationId) return;

                  const funilStages = stages.filter(s => s.funil === sourceId).sort((a, b) => a.ordem - b.ordem);
                  const [moved] = funilStages.splice(result.source.index, 1);
                  funilStages.splice(result.destination.index, 0, moved);

                  const updatedStages = stages.map(s => {
                    if (s.funil !== sourceId) return s;
                    const newIndex = funilStages.findIndex(fs => fs.id === s.id);
                    return { ...s, ordem: newIndex + 1 };
                  });

                  setStages(updatedStages);

                  try {
                    const payload = updatedStages.filter(s => s.funil === sourceId).map(({ id, ordem }) => ({ id, ordem }));
                    await fetch(`${API_URL}/reordenar_etapas`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ id_cliente: selectedTimeId, etapas: payload }),
                    });
                  } catch (err) { message.error("Erro ao aplicar ordem"); }
                }}
              >
                <div>
                  {funnels.map((funil) => (
                    <div className="tp-funnel-item" key={funil.id}>
                      <div className="tp-funnel-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="d-flex align-items-center">
                          <h5 className="tp-funnel-name">
                            <span className="tp-funnel-icon"><i className="ti ti-filter" /></span>
                            {funil.page}
                          </h5>
                          <div className="tp-funnel-badges">
                            {closerAtivo && (
                              <div className="tp-closer-switch">
                                <div className="form-check form-switch mb-0">
                                  <input className="form-check-input" type="checkbox" checked={funil.closer === "Sim"} onChange={() => toggleCloserFunil(funil.page, funil.closer === "Sim")} />
                                  <label className="form-check-label">Closer</label>
                                </div>
                              </div>
                            )}
                            {closerAtivo && funil.closer === "Sim" && (
                              <span className="tp-badge badge-closer"><i className="ti ti-robot me-1" />Closer Ativo</span>
                            )}
                            {closerAtivo && funil.closer === "Sim" && !stages.some(s => s.funil === funil.page && s.atendimento_humano === "Sim") && (
                              <span className="tp-badge badge-warning"><i className="ti ti-alert-triangle me-1" />Sem etapa humana</span>
                            )}
                            {/* Funil receptor: onde caem os leads que chegam pelo WhatsApp */}
                            {funil.receptor_wp === "Sim" && (
                              <span className="tp-badge badge-receptor" title="Os leads que chegam pelo WhatsApp entram neste funil">
                                <i className="ti ti-brand-whatsapp me-1" />RECEPTOR WHATSAPP
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="tp-funnel-actions">
                          {funil.receptor_wp !== "Sim" && (
                            <button
                              className="tp-funnel-btn"
                              title="Definir como funil receptor de WhatsApp"
                              onClick={() => definirReceptorWp(funil.page)}
                            ><i className="ti ti-brand-whatsapp" /></button>
                          )}
                          <button className="tp-funnel-btn" onClick={() => { setIsEdit(true); openEditFunilModal(funil.page); }}><i className="ti ti-edit" /></button>
                          <button className="tp-funnel-btn btn-danger-glass" onClick={() => handleDeleteFunil(funil.page)}><i className="ti ti-trash" /></button>
                          <button className="tp-new-stage-btn" onClick={() => openCreateEtapaModal(funil.page)}><i className="ti ti-plus" /> Nova Etapa</button>
                        </div>
                      </div>
                      <div className="tp-funnel-body">
                        <Droppable droppableId={funil.page}>
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                              {stages
                                .filter(s => s.funil === funil.page)
                                .sort((a, b) => a.ordem - b.ordem)
                                .map((stage, index) => (
                                  <Draggable key={`stage-${stage.id}`} draggableId={`stage-${stage.id}`} index={index}>
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={`tp-stage-row ${snapshot.isDragging ? 'is-dragging' : ''}`}
                                        style={{ ...provided.draggableProps.style, pointerEvents: 'auto' }}
                                      >
                                        <div {...provided.dragHandleProps} className="tp-grip"><i className="ti ti-grip-vertical" /></div>
                                        <div className="tp-stage-info" onClick={() => { setCurrentStage(stage.nome_funil); setCurrentFunnel(funil.page); setActiveModal("cadencia"); setTimeout(() => setModalShow(true), 200); }} style={{ cursor: 'pointer' }}>
                                          <h6 className="tp-stage-name">{stage.nome_funil}</h6>
                                          <p className="tp-stage-hint">Clique para editar mensagens</p>
                                        </div>
                                        <div className="tp-stage-badges">
                                          {stage.atendimento_humano === "Sim" && <span className="tp-badge-human">HUMANO</span>}
                                          {stage.atendimento_finalizado === "Sim" && <span className="tp-badge-human" style={{ background: "#e8f5e9", color: "#1b7f3b" }}>FINALIZADO</span>}
                                          {!!stage.closer && <span className="tp-badge-closer">CLOSER</span>}
                                        </div>
                                        <div className="tp-stage-actions">
                                          {closerAtivo && (
                                            <>
                                              {stage.atendimento_humano !== "Sim" && (
                                                <div className="tp-closer-switch-sm">
                                                  <div className="form-check form-switch mb-0">
                                                    <input className="form-check-input" type="checkbox" checked={!!stage.closer} onChange={() => toggleCloserEtapa(stage.id, !!stage.closer, stage.nome_funil, stage.funil)} />
                                                  </div>
                                                </div>
                                              )}
                                              <button className={`tp-stage-btn btn-human ${stage.atendimento_humano === "Sim" ? 'active' : ''}`} onClick={() => definirEtapaAtendimentoHumano(stage.nome_funil, stage.funil)}>
                                                <i className="ti ti-user-check" />
                                              </button>
                                            </>
                                          )}
                                          <button className={`tp-stage-btn btn-human ${stage.atendimento_finalizado === "Sim" ? 'active' : ''}`} title={stage.atendimento_finalizado === "Sim" ? "Etapa de atendimento finalizado (clique para desmarcar)" : "Marcar como etapa de atendimento finalizado"} onClick={() => definirEtapaAtendimentoFinalizado(stage.nome_funil, stage.funil, stage.atendimento_finalizado === "Sim")}>
                                            <i className="ti ti-circle-check" />
                                          </button>
                                          <button className="tp-stage-btn" onClick={() => { setIsEdit(true); openEditEtapaModal(stage.nome_funil, funil.page); }}><i className="ti ti-edit" /></button>
                                          <button className="tp-stage-btn btn-danger" onClick={() => handleDeleteEtapa(funil.page, stage.nome_funil)}><i className="ti ti-trash" /></button>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    </div>
                  ))}
                </div>
              </DragDropContext>
            </div>
          </div>
        )}
      </div>
      <div className="tp-footer">
        <p>2025 &copy; Conversai CRM</p>
      </div>

      {activeModal === 'cadencia' && (
        <CadenciaModal
          key={`${currentFunnel}-${currentStage}`}
          currentStage={currentStage}
          currentFunnel={currentFunnel}
          allCadencias={allCadencias}
          allStages={stages}
          show={modalShow}
          onHide={() => { setModalShow(false); setActiveModal(null); }}
          verificarCloser={closerAtivo}
          idClienteOverride={selectedTimeId}
          ehOficial={(() => { const tt = times.find((t: any) => String(t.id) === String(selectedTimeId)); return !!(tt && tt.fone && tt.ins); })()}
          onSaved={() => { fetchFunnels(); fetchStages(); fetchAllCadencias(true); }}
        />
      )}
      {activeModal === 'createEdit' && (
        <CreateEditModal
          show={modalShow}
          onHide={() => { setModalShow(false); setActiveModal(null); }}
          modalType={modalType}
          isEdit={isEdit}
          initialName={initialName}
          currentFunnel={modalType === 'etapa' ? currentFunnel : undefined}
          closerAtivo={closerAtivo}
          idClienteOverride={selectedTimeId}
          onSaved={() => { fetchFunnels(); fetchStages(); }}
        />
      )}
    </div>
  );
};

export default TodoAdmin;
